import React, { useEffect, useMemo, useState } from 'react';
import { InputNumber, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, LeftOutlined, RightOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { MarketSnapshot, DROP_SEVERITY_LABEL, DROP_SEVERITY_RANK, DropSeverity } from '../../types/card';
import { FONT_SIZE, FONT_WEIGHT, ICON_SIZE, SPACING, RADIUS, LINE_HEIGHT, isDrop } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useStorePriceName } from '../../hooks/UseStorePriceName';
import { useCardPage } from '../../contexts/CardPageContext';
import { useCardEdit } from '../../contexts/CardEditContext';
import { useStorage } from '../../contexts/StorageContext';
import { READ_ONLY_REASON } from '../../constants/readOnly';
import { AppButton } from '../common/AppButton';
import { AppModal } from '../common/AppModal';
import { AppPopconfirm } from '../common/AppPopconfirm';
import { formatPrice, formatThousands, parseThousands } from '../../utils/format';
import { fetchMarketItems } from '../../api/api';
import { appMessage as message } from '../../utils/appMessage';
import { MarketRangeBar } from '../dropClassification/MarketRangeBar';
import { useIsPhone } from '../../hooks/UseIsCompact';

const fmt = formatPrice;

interface Props {
  open: boolean;
  onClose: () => void;
}

// ─── DropReviewModal ──────────────────────────────────────────────────────────

/**
 * 하락 가격 확인 — 타 샵 시세 대조 분류에서 SUSPECT(오등록 의심)로 분류된 카드만
 * 심각도 순으로 한 건씩 검토한다("하락 검토 개선안", 2026-08-27). NO_REFERENCE(검증 불가)는 대기에서 바로 빠진다.
 * IN_RANGE(시세 범위 내)는 맨 위 한 줄의 "N건 일괄 적용 · 되돌리기" 로 처리한다 — 분류 카드가 "매장가 요청" 메뉴로
 * 옮겨 간 뒤 가격 조회 화면에서 시세 범위 내 대기를 풀 길이 없어 ③ 이 막혔다(사용자 결정 2026-10-26 ⓐ, 감사 #117).
 *
 * 인라인 ✓/✗(CardTable)와 동일한 pendingConfirmIds를 공유해 상태가 일관된다.
 * 키보드: Enter 적용 · Del 유지 · ←→ 이동.
 */
export const DropReviewModal: React.FC<Props> = ({ open, onClose }) => {
  const { cards: cardData, reviewFocusId } = useCardPage();
  const edit = useCardEdit();
  const t = useThemeTokens();
  const sp = useStorePriceName();
  // 조회 전용(VIEWER)은 보기만 — 적용 · 유지 · 일괄 · 되돌리기 · 입력을 잠근다(유저 플로우 제안 7). 이전 · 다음 이동은 된다.
  const { isAdmin } = useStorage();
  const readOnly = !isAdmin;
  const roReason = readOnly ? READ_ONLY_REASON : undefined;

  const allCards = cardData.cards;

  // 심각도(오등록 의심 강도) → 변동폭 큰 순으로 정렬한 검토 큐
  const queue = useMemo(() => {
    return allCards
      .filter((c) => edit.pendingConfirmIds.has(c.id) && c.dropBucket === 'SUSPECT')
      .sort((a, b) => {
        const r = DROP_SEVERITY_RANK[a.dropSeverity ?? 'MINOR'] - DROP_SEVERITY_RANK[b.dropSeverity ?? 'MINOR'];
        if (r !== 0) return r;
        return Math.abs(b.priceChangeRate ?? 0) - Math.abs(a.priceChangeRate ?? 0);
      });
  }, [allCards, edit.pendingConfirmIds]);

  const minorOnes = useMemo(() => queue.filter((c) => c.dropSeverity === 'MINOR'), [queue]);

  // 시세 범위 내(IN_RANGE) 하락 — 분류 카드의 "N건 일괄 적용 · 되돌리기" 와 같은 동작(감사 #117 ⓐ).
  const inRangeDrops = useMemo(
    () => allCards.filter((c) => isDrop(c.priceChangeStatus) && c.dropBucket === 'IN_RANGE'),
    [allCards],
  );
  const inRangePending = useMemo(
    () => inRangeDrops.filter((c) => edit.pendingConfirmIds.has(c.id)),
    [inRangeDrops, edit.pendingConfirmIds],
  );

  const [cursor, setCursor]     = useState(0);
  const [override, setOverride] = useState<number | null>(null);
  const [editing, setEditing]   = useState(false);
  const [busy, setBusy]         = useState(false);

  const [snapshot, setSnapshot]               = useState<MarketSnapshot | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError]     = useState(false);

  const idx     = queue.length === 0 ? 0 : Math.min(cursor, queue.length - 1);
  const current = queue[idx];

  // 열 때 표에서 누른 카드가 있으면 그 카드부터(감사 #457), 없으면 큐 맨 앞.
  useEffect(() => {
    if (!open) return;
    const at = reviewFocusId != null ? queue.findIndex((c) => c.id === reviewFocusId) : -1;
    setCursor(at >= 0 ? at : 0);
  }, [open, reviewFocusId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cursor > queue.length - 1) setCursor(Math.max(0, queue.length - 1));
  }, [queue.length, cursor]);

  useEffect(() => {
    if (current) { setOverride(current.suggestedPrice); setEditing(false); }
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 포커스된 카드가 바뀌면 PriceHub 판매처 목록을 다시 조회한다(하락 판단의 근거).
  // 404(PriceHub에 없음)는 조용히 숨기고, 그 외 에러만 별도 안내한다(2026-08-07).
  useEffect(() => {
    if (!current) { setSnapshot(null); setSnapshotError(false); return; }
    let cancelled = false;
    setSnapshotLoading(true);
    setSnapshot(null);
    setSnapshotError(false);
    fetchMarketItems(current.id)
      .then((data) => { if (!cancelled) setSnapshot(data); })
      .catch(() => { if (!cancelled) setSnapshotError(true); })
      .finally(() => { if (!cancelled) setSnapshotLoading(false); });
    return () => { cancelled = true; };
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const goNext = () => setCursor((c) => Math.min(c + 1, queue.length - 1));
  const goPrev = () => setCursor((c) => Math.max(c - 1, 0));

  // 저장이 도는 동안(한 건 · 일괄) 모든 처리 · 이동을 잠근다 — 한 건 저장 중 일괄을 누르면 저장이 실패한 카드까지 대기에서
  // 빠질 수 있었다(감사 #459).
  const locked = busy || edit.bulkSaving || edit.savingId != null;
  // 쓰기 잠금 — 저장 중이거나 조회 전용.
  const writeLocked = locked || readOnly;
  // 금액을 지운 채(빈 입력) 적용하면 <매장> 가격이 승인됐다(감사 #458) — 입력 중이면 유효한 금액(1원 이상)이어야 한다.
  const overrideInvalid = editing && (override == null || !Number.isFinite(override) || override <= 0);

  const apply = async () => {
    if (!current || writeLocked) return;
    if (overrideInvalid) {
      message.warning('적용할 가격을 입력해 주세요.');
      return;
    }
    setBusy(true);
    await edit.applyDrop(current, editing ? (override as number) : undefined);
    setBusy(false);
  };

  const keep = async () => {
    if (!current || writeLocked) return;
    setBusy(true);
    await edit.keepDrop(current);
    setBusy(false);
  };

  // 키보드 단축키 — 입력 필드 안에서는 이동/유지 키를 가로채지 않는다.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const inField = tag === 'INPUT' || tag === 'TEXTAREA';
      // 길게 누른 키(반복)는 무시 — 새로 나타난 다음 카드까지 연속 승인됐다(감사 #460).
      if (e.repeat && (e.key === 'Enter' || e.key === 'Delete' || e.key === 'Backspace')) return;
      if (e.key === 'Enter') {
        // 버튼 · 링크에 포커스가 있으면 그 버튼이 Enter 를 받는다 — 새 '시세 범위 내 일괄 적용' · '되돌리기' 에서 누른 Enter 가
        // 지금 확인 필요 카드 적용으로 먹혔다(코덱 리뷰 #209-7, 감사 card-controltower#287).
        if ((e.target as HTMLElement | null)?.closest?.('button, a, [role="button"]')) return;
        e.preventDefault(); apply(); return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (inField) return;
        e.preventDefault(); keep(); return;
      }
      // 저장 · 적용 확인 창(감사 #692)이 도는 동안은 버튼처럼 이동도 잠근다 — 확인 창 뒤에서 다른 카드로 넘어가 보였다.
      if (e.key === 'ArrowRight') { if (inField || locked) return; e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft')  { if (inField || locked) return; e.preventDefault(); goPrev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, current, editing, override, busy, locked, overrideInvalid, queue.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = queue.length === 0;
  // 휴대폰 폭(≤640) — 본문 326px 에 내용 480px 가 들어가 부산 가격 · 시세 막대 · 입력이 잘렸다(코덱 화면 점검 2026-10-30 높음,
  // 감사 #518): 한 열로 쌓는다. 버튼 44px · 조작 줄 쌓기는 index.css 의 .drop-review-modal · .drop-review-controls.
  const phone = useIsPhone();

  // 시세 범위 내 한 줄 — 확인 필요가 0건이어도 창이 비어 보이지 않게 맨 위에 먼저 둔다.
  const inRangeRow = inRangeDrops.length === 0 ? null : (
    <div
      data-testid="in-range-bulk"
      style={{
        display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap',
        border: `1px solid ${t.lineSuccess}`, background: t.tintSuccess, borderRadius: RADIUS.md,
        padding: `${SPACING.xs}px ${SPACING.sm}px`, marginBottom: SPACING.md,
      }}
    >
      <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary, flex: 1, minWidth: 200 }}>
        <b style={{ color: t.success }}>시세 범위 내 {inRangeDrops.length.toLocaleString()}건</b>
        {` — ${sp}이 다른 샵 판매가 최저~최고 사이라 한 건씩 보지 않고 일괄 적용해도 됩니다.`}
      </span>
      {inRangePending.length > 0 ? (
        <AppButton
          variant="primary"
          disabled={writeLocked}
          disabledReason={roReason}
          style={writeLocked ? undefined : { background: t.successSolid, borderColor: t.successSolid }}
          onClick={() => { if (!writeLocked) edit.applyDropsBulk(inRangePending); }}
        >
          {`시세 범위 내 ${inRangePending.length.toLocaleString()}건 일괄 적용`}
        </AppButton>
      ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: SPACING.xs, fontSize: FONT_SIZE.sm, color: t.success, fontWeight: FONT_WEIGHT.semibold }}>
          {`✓ ${inRangeDrops.length.toLocaleString()}건 적용됨`}
          <AppButton variant="link" size="sm" disabled={writeLocked} disabledReason={roReason}
            onClick={() => { if (!writeLocked) edit.restorePendingMany(inRangeDrops.map((c) => c.id)); }}>
            되돌리기
          </AppButton>
        </span>
      )}
    </div>
  );

  const sevColor = (s: DropSeverity | null) =>
    s === 'SEVERE' ? t.danger : s === 'MODERATE' ? t.warning : t.textSecondary;

  const shopPrices = snapshot?.items.map((i) => i.price).filter((p): p is number => p != null && p > 0) ?? [];
  const marketMin = shopPrices.length ? Math.min(...shopPrices) : current?.marketMin ?? null;
  const marketMax = shopPrices.length ? Math.max(...shopPrices) : current?.marketMax ?? null;
  const inRange = override != null && marketMin != null && marketMax != null
    && override >= marketMin && override <= marketMax;

  return (
    <AppModal
      open={open}
      title={done ? (inRangePending.length > 0 ? '하락 검토' : '하락 확인 완료') : (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap', paddingRight: phone ? 40 : 0 /* 휴대폰 닫기(X) 44px 자리 */ }}>
          <span>하락 가격 확인</span>
          {current && (
            <span style={{
              fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color: sevColor(current.dropSeverity),
              border: `1px solid ${sevColor(current.dropSeverity)}`, borderRadius: RADIUS.sm, padding: '1px 7px',
            }}>
              {current.dropSeverity ? DROP_SEVERITY_LABEL[current.dropSeverity] : '의심'}
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: FONT_SIZE.sm, color: t.textMuted, fontWeight: FONT_WEIGHT.semibold }}>
            {idx + 1} / {queue.length} · 남은 {queue.length}건
          </span>
        </div>
      )}
      onCancel={onClose}
      width={1000}
      className="drop-review-modal"
      footer={null}
    >
      {inRangeRow}
      {done ? (
        <div style={{ textAlign: 'center', padding: `${SPACING.xl}px ${SPACING.md}px` }}>
          <CheckCircleOutlined style={{ fontSize: ICON_SIZE.xxl, color: t.success }} />
          <div style={{ marginTop: SPACING.sm, fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>
            확인이 필요한 하락 카드가 없습니다
          </div>
          <div style={{ marginTop: SPACING.xxs, fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
            {inRangePending.length > 0
              ? '위의 시세 범위 내 일괄 적용까지 마치면 ③ 스토어 반영을 할 수 있습니다.'
              : '③ 스토어 반영으로 넘어가세요.'}
          </div>
          <div style={{ marginTop: SPACING.lg }}>
            <AppButton variant="primary" onClick={onClose}>닫기</AppButton>
          </div>
        </div>
      ) : current && (
        <div>
          {/* 일괄 처리 */}
          <div style={{
            display: 'flex', gap: SPACING.xs, flexWrap: 'wrap',
            paddingBottom: SPACING.sm, marginBottom: SPACING.md, borderBottom: `1px solid ${t.border}`,
          }}>
            {minorOnes.length > 0 && (
              <AppPopconfirm
                title={`경미 ${minorOnes.length}건을 ${sp}으로 일괄 적용할까요?`}
                okText="일괄 적용" danger={false}
                onConfirm={() => { if (!writeLocked) edit.applyDropsBulk(minorOnes); }}
                disabled={writeLocked}
              >
                <AppButton variant="default" size="sm" disabled={writeLocked} disabledReason={roReason}>경미 {minorOnes.length}건 일괄 적용</AppButton>
              </AppPopconfirm>
            )}
            {/* 반대쪽(전체 <매장> 가격으로 적용)이 없으면 "전부 <매장> 가격으로 간다"는 판단이 선 날에도
                한 건씩 Enter를 눌러야 한다 — 2026-08-27 모달 재설계 때 빠졌던 걸 되살린 것
                (2026-09-21, 사용자 요청). <매장> 가격 적용은 저장 호출 없이 대기 목록에서만
                빼면 된다(<매장> 가격이 이미 카드의 suggestedPrice이므로) — keepDropsBulk와 달리
                updatePriceManually를 부르지 않는 이유. */}
            <AppPopconfirm
              title={`남은 ${queue.length}건을 모두 ${sp}으로 적용할까요?`}
              okText={`전체 ${sp}으로 적용`} danger={false}
              onConfirm={() => { if (!writeLocked) edit.applyDropsBulk(queue); }}
              disabled={writeLocked}
            >
              <AppButton variant="default" size="sm" disabled={writeLocked} disabledReason={roReason}>남은 전체 {sp}으로 적용</AppButton>
            </AppPopconfirm>
            <AppPopconfirm
              title={`남은 ${queue.length}건을 모두 네이버 가격으로 유지할까요?`}
              okText="전체 네이버 가격 유지"
              onConfirm={() => { if (!writeLocked) void edit.keepDropsBulk(queue); }}
              disabled={writeLocked}
            >
              <AppButton variant="ghost-danger" size="sm" loading={edit.bulkSaving}
                disabled={readOnly || (locked && !edit.bulkSaving)} disabledReason={roReason}>남은 전체 네이버 가격 유지</AppButton>
            </AppPopconfirm>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : '150px 1fr', gap: phone ? SPACING.sm : SPACING.lg }}>
            {/* 카드 이미지 — 휴대폰은 작은 이미지 옆에 코드 · 재고(한 열 격자의 첫 줄) */}
            <div style={phone ? { display: 'flex', alignItems: 'flex-end', gap: SPACING.sm } : undefined}>
              {current.imageUrl ? (
                <img
                  src={current.imageUrl} alt={current.productName}
                  style={{ width: phone ? 72 : 150, height: phone ? 100 : 210, objectFit: 'contain', borderRadius: RADIUS.md, background: t.bgPlaceholder, border: `1px solid ${t.border}` }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div style={{ width: phone ? 72 : 150, height: phone ? 100 : 210, borderRadius: RADIUS.md, background: t.bgPlaceholder, border: `1px solid ${t.border}`, flexShrink: 0 }} />
              )}
              <div style={{ marginTop: SPACING.xs, fontSize: FONT_SIZE.xs, color: t.textMuted, lineHeight: LINE_HEIGHT.relaxed }}>
                {current.sellerProductCode}<br />
                재고 {current.stockQuantity?.toLocaleString('ko-KR') ?? '-'}
              </div>
            </div>

            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary, lineHeight: LINE_HEIGHT.normal }}>
                {current.productName}
              </div>

              {/* 의심 사유 배너 */}
              <div style={{
                marginTop: SPACING.sm, border: `1px solid ${sevColor(current.dropSeverity)}`,
                background: current.dropSeverity === 'MINOR' ? t.bgRaised : t.tintDanger,
                borderRadius: RADIUS.md, padding: `${SPACING.xs}px ${SPACING.sm}px`,
              }}>
                <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: sevColor(current.dropSeverity) }}>
                  ⚠ {current.dropReason ?? '타 샵 시세를 벗어남'}
                </span>
              </div>

              {/* 네이버 가격 → <매장> 가격 + 시세 막대 */}
              <div style={{ marginTop: SPACING.md, display: 'flex', alignItems: 'center', gap: SPACING.md }}>
                <div style={{ flexShrink: 0, textAlign: 'center', border: `1px solid ${t.border}`, background: t.bgPage, borderRadius: RADIUS.md, padding: `${SPACING.xs}px ${SPACING.md}px` }}>
                  <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>네이버 가격</div>
                  <div style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold, color: t.textSecondary }}>
                    {current.currentPrice != null ? fmt(current.currentPrice) : '-'}
                  </div>
                </div>
                <span style={{ color: t.textMuted }}>→</span>
                <div style={{ flexShrink: 0, textAlign: 'center', border: `1px solid ${t.lineDanger}`, background: t.tintDanger, borderRadius: RADIUS.md, padding: `${SPACING.xs}px ${SPACING.md}px` }}>
                  <div style={{ fontSize: FONT_SIZE.xs, color: t.danger }}>
                    {sp} {current.priceChangeRate != null && <b>▼{Math.abs(Math.round(current.priceChangeRate))}%</b>}
                  </div>
                  <div style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.bold, color: t.danger }}>
                    {current.suggestedPrice != null ? fmt(current.suggestedPrice) : '-'}
                  </div>
                </div>
                <div className="drop-range-wrap" style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginBottom: SPACING.xxs }}>
                    타 샵 판매가 {shopPrices.length || current.marketShopCount || 0}곳
                  </div>
                  <MarketRangeBar
                    marketMin={marketMin} marketMax={marketMax}
                    currentPrice={current.currentPrice} suggestedPrice={override}
                    height={30} showScale
                    suggestedLabel="적용할 가격"
                  />
                </div>
              </div>

              {/* 판매처별 가격 + 적용가 입력 */}
              <div style={{ marginTop: SPACING.md, display: 'grid', gridTemplateColumns: phone ? 'minmax(0, 1fr)' : '1fr 1fr', gap: SPACING.md }}>
                <MarketItemsPanel
                  snapshot={snapshot}
                  loading={snapshotLoading}
                  error={snapshotError}
                  onSelectPrice={(price) => { if (readOnly) return; setOverride(price); setEditing(true); }}
                />
                <div>
                  <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginBottom: SPACING.xxs }}>적용할 가격</div>
                  <InputNumber
                    size="large"
                    disabled={readOnly}
                    title={roReason}
                    value={override}
                    onChange={(v) => { setOverride(v); setEditing(true); }}
                    min={0}
                    style={{ width: '100%' }}
                    formatter={formatThousands}
                    parser={parseThousands}
                    addonAfter="원"
                  />
                  <div style={{ fontSize: FONT_SIZE.xs, marginTop: SPACING.xxs, color: inRange ? t.success : t.warning }}>
                    {marketMin != null && marketMax != null
                      ? inRange
                        ? `✓ 타 샵 시세 범위 안입니다 (${fmt(marketMin)} ~ ${fmt(marketMax)})`
                        : `⚠ 타 샵 시세 범위(${fmt(marketMin)} ~ ${fmt(marketMax)})를 벗어납니다`
                      : '대조할 타 샵 시세가 없습니다'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 컨트롤 — 휴대폰은 유지 · 적용을 맨 위 한 줄 전체 폭으로(index.css .drop-review-controls) */}
          <div className="drop-review-controls" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: SPACING.lg, paddingTop: SPACING.sm, borderTop: `1px solid ${t.border}`, gap: SPACING.xs,
          }}>
            <AppButton variant="text" size="sm" icon={<LeftOutlined />} disabled={idx === 0 || locked} onClick={goPrev}>
              이전
            </AppButton>
            <div style={{ display: 'flex', gap: SPACING.xs }}>
              <Tooltip title={readOnly ? undefined : '네이버 가격 유지 (Del)'}>
                <AppButton variant="ghost-danger" icon={<CloseOutlined />} loading={busy}
                  disabled={readOnly || (locked && !busy)} disabledReason={roReason} onClick={keep}>
                  {current.currentPrice != null ? `${current.currentPrice.toLocaleString('ko-KR')}원 유지` : '유지'}
                </AppButton>
              </Tooltip>
              <Tooltip title={readOnly ? undefined : editing ? '입력한 금액으로 적용 — 확인 창 뒤 바로 네이버 반영 (Enter)' : `${sp}으로 적용 (Enter)`}>
                <AppButton
                  variant="primary" icon={<CheckOutlined />} loading={busy} onClick={apply}
                  disabled={readOnly || overrideInvalid || (locked && !busy)}
                  disabledReason={roReason ?? (overrideInvalid ? '적용할 가격을 입력해 주세요' : undefined)}
                  style={readOnly || overrideInvalid ? undefined : { background: t.successSolid, borderColor: t.successSolid }}
                >
                  {override != null && override > 0 ? `${override.toLocaleString('ko-KR')}원 적용` : '적용'}
                </AppButton>
              </Tooltip>
            </div>
            <AppButton variant="text" size="sm" disabled={idx >= queue.length - 1 || locked} onClick={goNext}>
              다음 <RightOutlined />
            </AppButton>
          </div>

          {/* 조회 전용은 적용 · 유지 단축키가 막혀 있다 — 이동만 안내하고 잠금 이유를 글로(터치 기기는 비활성 버튼 툴팁이 잘 안 뜬다,
              시우 검수 PR #93 낮음). */}
          <div data-testid="drop-review-keys" style={{ marginTop: SPACING.sm, textAlign: 'center', fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
            {readOnly ? (
              <>{`${READ_ONLY_REASON} — 적용 · 유지는 관리자가 합니다`} · <kbd>←</kbd> <kbd>→</kbd> 이동</>
            ) : (
              <><kbd>Enter</kbd> 적용 · <kbd>Del</kbd> 유지 · <kbd>←</kbd> <kbd>→</kbd> 이동</>
            )}
          </div>
        </div>
      )}
    </AppModal>
  );
};

// ─── MarketItemsPanel ────────────────────────────────────────────────────────

/**
 * PriceHub 판매처 목록(price-snapshot) — 하락이 실제 시세 하락인지, 오매칭/이상치인지
 * 판단할 근거. 포커스된 카드가 바뀔 때마다 다시 조회된다. 404(PriceHub 미보유)는 조용히
 * 숨기고 그 외 에러만 안내한다(2026-08-07). 클릭하면 그 가격이 "적용할 가격"에 들어간다.
 */
const MarketItemsPanel: React.FC<{
  snapshot: MarketSnapshot | null;
  loading: boolean;
  error: boolean;
  onSelectPrice: (price: number) => void;
}> = ({ snapshot, loading, error, onSelectPrice }) => {
  const t = useThemeTokens();
  const phone = useIsPhone();

  const label = (children: React.ReactNode) => (
    <div>
      <div style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary, marginBottom: SPACING.xxs }}>
        판매처별 가격 <span style={{ color: t.textMuted }}>— 클릭하면 그 가격으로 설정</span>
      </div>
      {children}
    </div>
  );

  if (loading) return label(<div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>판매처 목록 불러오는 중...</div>);
  if (error) return label(<div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted, lineHeight: LINE_HEIGHT.relaxed }}>
    판매처 목록을 불러오지 못했습니다 (PriceHub 서버 오류/네트워크 — 카드 미보유와는 다릅니다). 검토는 계속 진행할 수 있습니다.
  </div>);
  if (!snapshot || snapshot.items.length === 0) return label(<div style={{ fontSize: FONT_SIZE.xs, color: t.textMuted }}>판매처 목록 없음</div>);

  return label(
    <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: SPACING.xxs }}>
      {snapshot.items.map((item, i) => (
        <button
          key={i}
          type="button"
          onClick={() => { if (item.price != null) onSelectPrice(item.price); }}
          style={{
            display: 'flex', alignItems: 'center', gap: SPACING.xs, width: '100%', textAlign: 'left',
            border: `1px solid ${t.border}`, background: t.bgSurface, borderRadius: RADIUS.md,
            padding: `${SPACING.xxs}px ${SPACING.xs}px`, cursor: 'pointer', fontSize: FONT_SIZE.sm, color: t.textPrimary,
            minHeight: phone ? 44 : undefined,
          }}
        >
          <div style={{ width: 32, height: 32, borderRadius: RADIUS.sm, background: t.bgPlaceholder, flexShrink: 0, overflow: 'hidden' }}>
            {item.image && (
              <img src={item.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            )}
          </div>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.mallName ?? '-'}
          </span>
          <span style={{ flexShrink: 0, fontWeight: FONT_WEIGHT.semibold }}>
            {item.price != null ? `${item.price.toLocaleString('ko-KR')}원` : '-'}
          </span>
        </button>
      ))}
    </div>,
  );
};
